declare const Sentry: { captureException(e: Error): void } | undefined;

const LOG_ENDPOINT = './api/client-errors';
const BATCH_SIZE = 10;
const FLUSH_INTERVAL = 10000;

type ExtraType =
  | null
  | typeof Object
  | Error
  | Partial<{
      status: number;
      url: string;
      method: string;
      data: string | typeof Object;
      exception: string;
      cause: string;
      from: string;
      to: string;
      source: string;
      lineno: number;
      colno: number;
    }>;

export interface ILog {
  level: 'info' | 'warn' | 'error' | 'debug';
  message: string;
  context: {
    timestamp: string;
    url: string;
    appVersion: string | null;
  };
  extra: ExtraType;
}

export interface ILoggerService {
  info(message: string, extra?: ExtraType): void;
  warn(message: string, extra?: ExtraType): void;
  error(message: string, extra?: ExtraType): void;
  flush(isUnload?: boolean): void;
}

class LoggerService implements ILoggerService {
  private logQueue: ILog[] = [];
  private intervalId: number | null = null;

  private appVersion: string | null = null;
  private $location: angular.ILocationService | null = null;
  private $http: angular.IHttpService | null = null;

  constructor(
    private $window: angular.IWindowService,
    private $injector: angular.auto.IInjectorService
  ) {
    if (this.$window?.addEventListener) {
      this.$window.addEventListener('unload', () => this.flush(true));
    }
  }

  private sanitize(message: string): string {
    return message.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  private normalizeMessage(message: any): string {
    let msg = '';

    if (typeof message === 'string') {
      msg = this.sanitize(message);
    } else {
      try {
        msg = this.sanitize(JSON.stringify(message));
      } catch {
        msg = 'Un-stringifiable message';
      }
    }
    return msg.substring(0, 4000);
  }

  private normalizeExtra(extra: ExtraType | undefined): any {
    if (extra == null) return {};

    if (typeof extra !== 'object') {
      return { value: this.normalizeMessage(extra) };
    }

    if (extra instanceof Error) {
      return { stack: extra.stack, message: extra.message };
    }

    try {
      return structuredClone(extra);
    } catch {
      return { value: 'Un-serializable extra' };
    }
  }

  /**
   * #app-version is rendered server-side in index.gsp and does not change for the life of the page,
   * so read it from there rather than through StorageService.getVersion(). StorageService reports
   * its own failures through this logger, so reading back through it makes logging re-entrant — a
   * corrupt mlt.version would recurse until the stack blows. It is also the earlier source: the
   * version only reaches storage once InitService has run.
   */
  private getAppVersion(): string | null {
    if (this.appVersion === null) {
      const element = this.$window.document?.getElementById('app-version') as HTMLInputElement | null;
      // `||` not `??`: an empty hidden input means the server rendered no version.
      this.appVersion = element?.value || null;
    }

    return this.appVersion;
  }

  private getContext() {
    const appVersion = this.getAppVersion();
    let url = '';

    try {
      this.$location ??= this.$injector.get('$location');
      url = this.$location.absUrl();
    } catch {
      url = this.$window.location.href;
    }

    return {
      timestamp: new Date().toISOString(),
      url,
      appVersion
    };
  }

  private startInterval(): void {
    this.intervalId ??= window.setInterval(() => this.flush(), FLUSH_INTERVAL);
  }

  private stopInterval(): void {
    if (this.intervalId) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private enqueue(level: 'info' | 'warn' | 'error', message: string, extra?: ExtraType) {
    if (extra !== undefined) {
      //eslint-disable-next-line no-console
      console[level](message, extra);
    } else {
      //eslint-disable-next-line no-console
      console[level](message);
    }

    this.startInterval();

    this.logQueue.push({
      level,
      message: this.normalizeMessage(message),
      context: this.getContext(),
      extra: this.normalizeExtra(extra)
    });

    if (this.logQueue.length >= BATCH_SIZE) {
      this.flush();
    }
  }

  info(message: string, extra?: ExtraType): void {
    this.enqueue('info', message, extra);
  }

  warn(message: string, extra?: ExtraType): void {
    this.enqueue('warn', message, extra);
  }

  error(message: string, extra?: ExtraType): void {
    this.enqueue('error', message, extra);
    if (typeof Sentry !== 'undefined' && typeof Sentry.captureException === 'function') {
      Sentry.captureException(extra instanceof Error ? extra : new Error(String(message)));
    }
  }

  flush(isUnload = false): void {
    if (this.logQueue.length === 0) {
      this.stopInterval();
      return;
    }

    const logsToSend = this.logQueue.splice(0, this.logQueue.length);
    const payload = { logs: logsToSend };

    if (isUnload && this.$window.navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      this.$window.navigator.sendBeacon(LOG_ENDPOINT, blob);
      return;
    }

    try {
      this.$http ??= this.$injector.get<angular.IHttpService>('$http');
      this.$http.post(LOG_ENDPOINT, payload);
    } catch (error) {
      console.error('Error flushing logs:', error);
    }
  }
}

export default LoggerService;
